import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MessageCircle, User, Star, Eye, Image } from "lucide-react";
import { TradesmanRatingsDialog } from "./TradesmanRatingsDialog";
import { TradesmanPortfolioDialog } from "./TradesmanPortfolioDialog";

interface TradesmanWithMessages {
  id: string;
  company_name?: string;
  trade_specialties?: string[];
  average_rating?: number;
  total_reviews?: number;
  unread_count: number;
  last_message_at: string;
}

interface JobTradesmenMessagesSectionProps {
  jobId: string;
  onStartChat: (tradesmanId: string) => void;
  onMessagesStatusChange?: (hasMessages: boolean) => void;
}

export const JobTradesmenMessagesSection = ({ jobId, onStartChat, onMessagesStatusChange }: JobTradesmenMessagesSectionProps) => {
  const [tradesmenWithMessages, setTradesmenWithMessages] = useState<TradesmanWithMessages[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showRatingsDialog, setShowRatingsDialog] = useState(false);
  const [showPortfolioDialog, setShowPortfolioDialog] = useState(false);
  const [selectedTradesmanId, setSelectedTradesmanId] = useState<string | null>(null);

  useEffect(() => {
    fetchTradesmenWithMessages();
  }, [jobId]);

  const fetchTradesmenWithMessages = async () => {
    setIsLoading(true);
    try {
      // Get current user's profile ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from("profiles")
        .select("id")
        .eq("user_id", user.id)
        .single();

      if (!profile) return;

      // Get messages for this job where the current user is the recipient
      const { data: messages, error } = await supabase
        .from("messages")
        .select("sender_id, created_at, id, is_read_by_recipient")
        .eq("job_id", jobId)
        .eq("recipient_id", profile.id)
        .order("created_at", { ascending: false });

      if (error) throw error;

      if (!messages || messages.length === 0) {
        setTradesmenWithMessages([]);
        onMessagesStatusChange?.(false);
        return;
      }

      // Group messages by sender and count unread ones
      const messagesByTradesman = new Map<string, { messages: any[], unread_count: number, last_message_at: string }>();
      
      for (const message of messages) {
        const senderId = message.sender_id;
        if (!messagesByTradesman.has(senderId)) {
          messagesByTradesman.set(senderId, {
            messages: [],
            unread_count: 0,
            last_message_at: message.created_at
          });
        }
        
        const senderData = messagesByTradesman.get(senderId)!;
        senderData.messages.push(message);
        
        // Count only unread messages
        if (!message.is_read_by_recipient) {
          senderData.unread_count += 1;
        }
        
        // Update last message time if this is more recent
        if (new Date(message.created_at) > new Date(senderData.last_message_at)) {
          senderData.last_message_at = message.created_at;
        }
      }

      // Get tradesman profiles for each sender
      const tradesmanIds = Array.from(messagesByTradesman.keys());
      const tradesmenData = await Promise.all(
        tradesmanIds.map(async (id) => {
          try {
            const { data } = await supabase.rpc("get_public_tradesman_profile", { tradesman_id: id });
            const row: any = Array.isArray(data) ? data[0] : data;
            const messageData = messagesByTradesman.get(id)!;
            
            if (row) {
              return {
                id: row.id,
                company_name: row.company_name,
                trade_specialties: row.trade_specialties,
                average_rating: row.average_rating ?? undefined,
                total_reviews: row.total_reviews ?? undefined,
                unread_count: messageData.unread_count,
                last_message_at: messageData.last_message_at
              } as TradesmanWithMessages;
            }
            return {
              id,
              unread_count: messageData.unread_count,
              last_message_at: messageData.last_message_at
            } as TradesmanWithMessages;
          } catch (e) {
            console.warn("Error fetching tradesman profile for", id, e);
            const messageData = messagesByTradesman.get(id)!;
            return {
              id,
              unread_count: messageData.unread_count,
              last_message_at: messageData.last_message_at
            } as TradesmanWithMessages;
          }
        })
      );

      // Sort by last message time (most recent first)
      const sortedTradesmen = tradesmenData
        .filter(t => t)
        .sort((a, b) => new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime());

      setTradesmenWithMessages(sortedTradesmen);
      onMessagesStatusChange?.(sortedTradesmen.length > 0);
    } catch (error) {
      console.error("Error fetching tradesmen with messages:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatLastMessage = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      return "Just now";
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return date.toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: '2-digit' 
      });
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" />
            Messages from Tradesmen
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-sm text-muted-foreground">Loading messages...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (tradesmenWithMessages.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" />
            Messages from Tradesmen
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4 text-muted-foreground">
            No messages from tradesmen yet
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageCircle className="h-5 w-5" />
          Messages from Tradesmen
          <Badge variant="secondary" className="text-xs">
            {tradesmenWithMessages.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 sm:space-y-3 p-3 sm:p-6">
        {tradesmenWithMessages.map((tradesman) => (
          <div
            key={tradesman.id}
            className="flex items-center justify-between p-2 sm:p-3 border rounded-lg hover:bg-accent/50 transition-colors"
          >
            <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
              <div className="relative">
                <User className="h-6 w-6 sm:h-8 sm:w-8 text-muted-foreground" />
                {tradesman.unread_count > 0 && (
                  <Badge 
                    variant="destructive" 
                    className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 h-4 w-4 sm:h-5 sm:w-5 rounded-full p-0 flex items-center justify-center text-xs"
                  >
                    {tradesman.unread_count}
                  </Badge>
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-xs sm:text-sm truncate">
                    {tradesman.company_name || 'Tradesman'}
                  </span>
                  {tradesman.average_rating && (
                    <div className="flex items-center gap-1">
                      <Star className="h-3 w-3 text-yellow-500 fill-current" />
                      <span className="text-xs text-muted-foreground">
                        {tradesman.average_rating.toFixed(1)}
                      </span>
                    </div>
                  )}
                </div>
                
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs text-muted-foreground truncate">
                    Last message: {formatLastMessage(tradesman.last_message_at)}
                  </div>
                  {tradesman.unread_count > 0 && (
                    <Badge variant="destructive" className="text-xs shrink-0">
                      {tradesman.unread_count} new
                    </Badge>
                  )}
                </div>
                
                {tradesman.trade_specialties && tradesman.trade_specialties.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {tradesman.trade_specialties.slice(0, 2).map((specialty) => (
                      <Badge key={specialty} variant="outline" className="text-xs">
                        {specialty}
                      </Badge>
                    ))}
                    {tradesman.trade_specialties.length > 2 && (
                      <Badge variant="outline" className="text-xs">
                        +{tradesman.trade_specialties.length - 2}
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setSelectedTradesmanId(tradesman.id);
                    setShowRatingsDialog(true);
                  }}
                  className="text-xs"
                >
                  <Star className="h-3 w-3 mr-1" />
                  <span className="hidden sm:inline">Ratings</span>
                </Button>
                
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setSelectedTradesmanId(tradesman.id);
                    setShowPortfolioDialog(true);
                  }}
                  className="text-xs"
                >
                  <Image className="h-3 w-3 mr-1" />
                  <span className="hidden sm:inline">Portfolio</span>
                </Button>
              </div>
              
              <Button
                size="sm"
                onClick={() => onStartChat(tradesman.id)}
                className="text-xs"
              >
                <MessageCircle className="h-3 w-3 mr-1" />
                <span className="hidden sm:inline">Chat</span>
              </Button>
            </div>
          </div>
        ))}
        
        {/* Dialogs */}
        <TradesmanRatingsDialog
          open={showRatingsDialog}
          onOpenChange={setShowRatingsDialog}
          tradesmanId={selectedTradesmanId}
        />
        
        <TradesmanPortfolioDialog
          open={showPortfolioDialog}
          onOpenChange={setShowPortfolioDialog}
          tradesmanId={selectedTradesmanId}
        />
      </CardContent>
    </Card>
  );
};